import React from 'react';
import { Shield, Users, Crown, Lock } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from 'lift-ui';
import '../iam-support.styles.scss';

interface InfoCardsProps {
  totalUsers?: number;
  superadminCount?: number;
  totalGroups?: number;
  totalProducts?: number;
}

const InfoCards: React.FC<InfoCardsProps> = ({
  totalUsers,
  superadminCount,
  totalGroups,
  totalProducts,
}) => {
  return (
    <div className="iam-support-cards iam-support-cards--equal-height">
      <Card shadow="medium" className="iam-support-card">
        <CardHeader showIcon={true} icon={<Shield className="iam-support-icon" />} showMoreIcon={false}>
          <CardTitle>Products & Permissions</CardTitle>
        </CardHeader>
        <CardContent className="iam-support-card-content">
          <div className="iam-support-card-body">
            <CardDescription className="iam-support-card-description">
              Manage access control for different products with three permission levels:
            </CardDescription>
            <ul className="iam-support-list">
              <li><strong>Viewer:</strong> View Tables and Reports</li>
              <li><strong>Editor:</strong> View and add/update data</li>
              <li><strong>Manager:</strong> Editor permissions + Delete data</li>
            </ul>
          </div>
          {totalProducts !== undefined && (
            <div className="iam-support-card-footer">
              <span className="text-2xl font-bold text-foreground tabular-nums">{totalProducts}</span>
              <span className="text-sm text-muted-foreground">products configured</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card shadow="medium" className="iam-support-card">
        <CardHeader showIcon={true} icon={<Users className="iam-support-icon" />} showMoreIcon={false}>
          <CardTitle>User Groups</CardTitle>
        </CardHeader>
        <CardContent className="iam-support-card-content">
          <div className="iam-support-card-body">
            <CardDescription className="iam-support-card-description">
              Organize users into groups for easier permission management:
            </CardDescription>
            <ul className="iam-support-list">
              <li>Create and manage user groups</li>
              <li>Assign permissions to groups</li>
              <li>Add or remove users from groups</li>
            </ul>
          </div>
          {totalGroups !== undefined && (
            <div className="iam-support-card-footer">
              <span className="text-2xl font-bold text-foreground tabular-nums">{totalGroups}</span>
              <span className="text-sm text-muted-foreground">groups created</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card shadow="medium" className="iam-support-card">
        <CardHeader showIcon={true} icon={<Crown className="iam-support-icon" />} showMoreIcon={false}>
          <CardTitle>Superadmins</CardTitle>
        </CardHeader>
        <CardContent className="iam-support-card-content">
          <div className="iam-support-card-body">
            <CardDescription className="iam-support-card-description">
              Users with unrestricted access to everything in LIFT Admin:
            </CardDescription>
            <ul className="iam-support-list">
              <li>Full system access</li>
              <li>Can promote other users to superadmin</li>
              <li>Only superadmins can manage superadmin roles</li>
            </ul>
          </div>
          {superadminCount !== undefined && (
            <div className="iam-support-card-footer">
              <span className="text-2xl font-bold text-foreground tabular-nums">{superadminCount}</span>
              <span className="text-sm text-muted-foreground">superadmin{superadminCount !== 1 ? 's' : ''}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card shadow="medium" className="iam-support-card">
        <CardHeader showIcon={true} icon={<Lock className="iam-support-icon" />} showMoreIcon={false}>
          <CardTitle>Permission System</CardTitle>
        </CardHeader>
        <CardContent className="iam-support-card-content">
          <div className="iam-support-card-body">
            <CardDescription className="iam-support-card-description">
              How permissions are determined:
            </CardDescription>
            <ul className="iam-support-list">
              <li>Direct user permissions</li>
              <li>Group-based permissions</li>
              <li>Highest permission level is applied</li>
            </ul>
          </div>
          {totalUsers !== undefined && (
            <div className="iam-support-card-footer">
              <span className="text-2xl font-bold text-foreground tabular-nums">{totalUsers}</span>
              <span className="text-sm text-muted-foreground">total users</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InfoCards;
